import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { Midi, MidiNote, MidiTimeSignature } from "@midi-fantom-utils/midi-core";
import { measureBoundaries } from "@midi-fantom-utils/midi-util";
import { PIANO_ROLL_COLORS } from "../pianoRollColors.js";
import { DRUM_COLOR, channelColor } from "../channelColors.js";
import {
  clamp,
  lowerBound,
  resizeCanvas,
  signatureAt,
  useRafFrame,
  strokeHGroup,
  strokeVGroup,
  renderTimeGrid,
  renderVGridCache,
  blitVGridCache,
  vGridCacheNeedsRender,
  ensureVGridCache,
  createVGridCache,
  type VGridCache,
  type TimeGridLevels,
  IS_DEV,
  profileDrawFrame,
} from "../canvas-utils.js";
import s from "./PianoRoll.module.css";

export type PianoRollMode = "view" | "editor";

export type PianoRollTool = "draw" | "erase" | "select";

/** Метка (маркер) на полоске над тайм-линейкой. `measure` — 1-based такт. */
export type PianoRollMarker = {
  id: string;
  measure: number;
  name: string; // пустая строка, пока не переименована
};

export type PianoRollProps = {
  midi: Midi;
  width: number;
  height: number;
  mode?: PianoRollMode;
  /** Текущая позиция playhead в тиках (для отображения вертикальной линии). */
  playheadTick?: number;
  /** Клик по нотам/холсту — переход playhead (view-режим). */
  onSeek?: (tick: number) => void;
  /** Какие каналы показывать (view-режим). undefined = все. */
  enabledChannels?: ReadonlySet<number>;
  /** Высота полоски меток, px. */
  markerStripHeight?: number;
  /** Метки над тайм-линейкой (view-режим). */
  markers?: ReadonlyArray<PianoRollMarker>;
  onAddMarker?: (tick: number) => void;
  onMoveMarker?: (id: string, tick: number) => void;
  onRenameMarker?: (id: string, name: string) => void;
  onDeleteMarker?: (id: string) => void;
  /** Колбэки редактирования (editor-режим). */
  onAddNote?: (note: Omit<MidiNote, "channel">) => void;
  onMoveNote?: (noteId: number, ticks: number, midi: number) => void;
  onResizeNote?: (noteId: number, durationTicks: number) => void;
  onEraseNote?: (noteId: number) => void;
  /** Активный snap (дробление 1/N ноты). null = off. */
  snapDenominator?: number | null;
  /** Активный инструмент (editor-режим). */
  tool?: PianoRollTool;
};

const MIN_PX_PER_BEAT = 2;
const MAX_PX_PER_BEAT = 256;
const MIN_PX_PER_BEAT_FIT = 4;
const MIN_PX_PER_KEY = 2;
const MAX_PX_PER_KEY = 48;
const MIN_PX_PER_KEY_FIT = 4;
const KEY_RAIL_W = 56;
const RULER_H = 20;
const DEFAULT_MARKER_STRIP_H = 22;
const ZOOM_STEP = 1.25;
const ZOOM_WHEEL_FACTOR = 0.0015;
/** Запас видимой области в пикселях (в долях такта / октавах) при каллинге. */
const PAD_MEASURES = 1;
const PAD_OCTAVES = 1;
/** Горизонтальный запас offscreen-кэша вертикальной сетки, px. */
const GRID_CACHE_MARGIN = 160;
const SCROLLBAR_MIN = 20;
/** Порог движения мыши (px) до перехода click → pan. */
const PAN_THRESHOLD = 3;

const BLACK_KEYS = new Set([1, 3, 6, 8, 10]);

type ViewState = {
  pxPerBeat: number; // [MIN_PX_PER_BEAT, MAX_PX_PER_BEAT]
  pxPerKey: number; // [MIN_PX_PER_KEY, MAX_PX_PER_KEY]
  scrollXPx: number; // ≥0, ≤ totalPx - viewportW
  scrollYPx: number; // ≥0, ≤ 128*pxPerKey - viewportH
};

type DisplayNote = { note: MidiNote; channel: number };

const clampViewState = (
  v: ViewState,
  viewport: { w: number; h: number },
  boundaries: number[],
  ppq: number
): ViewState => {
  const pxPerTick = v.pxPerBeat / ppq;
  const totalPx = (boundaries[boundaries.length - 1] ?? 0) * pxPerTick;
  const totalYPx = 128 * v.pxPerKey;
  return {
    pxPerBeat: clamp(v.pxPerBeat, MIN_PX_PER_BEAT, MAX_PX_PER_BEAT),
    pxPerKey: clamp(v.pxPerKey, MIN_PX_PER_KEY, MAX_PX_PER_KEY),
    scrollXPx: clamp(v.scrollXPx, 0, Math.max(0, totalPx - viewport.w)),
    scrollYPx: clamp(v.scrollYPx, 0, Math.max(0, totalYPx - viewport.h)),
  };
};

const computePitchRange = (notes: ReadonlyArray<DisplayNote>): { min: number; max: number } => {
  if (notes.length === 0) return { min: 48, max: 72 };
  let lo = 127;
  let hi = 0;
  for (const d of notes) {
    const m = d.note.midi;
    if (m < lo) lo = m;
    if (m > hi) hi = m;
  }
  lo = Math.max(0, lo - 2);
  hi = Math.min(127, hi + 2);
  if (hi - lo < 12) {
    const mid = (hi + lo) / 2;
    lo = Math.max(0, Math.floor(mid - 6));
    hi = Math.min(127, Math.ceil(mid + 6));
  }
  return { min: lo, max: hi };
};

const computeFit = (
  viewport: { w: number; h: number },
  boundaries: number[],
  ppq: number,
  notes: ReadonlyArray<DisplayNote>
): ViewState => {
  const lastBoundary = boundaries[boundaries.length - 1] ?? 0;
  const pxPerBeat = clamp(
    (viewport.w * ppq) / Math.max(1, lastBoundary),
    MIN_PX_PER_BEAT_FIT,
    MAX_PX_PER_BEAT
  );
  const range = computePitchRange(notes);
  const rows = Math.max(1, range.max - range.min + 1);
  const pxPerKey = clamp(viewport.h / rows, MIN_PX_PER_KEY_FIT, MAX_PX_PER_KEY);
  const totalYPx = 128 * pxPerKey;
  // Центрируем диапазон нот по вертикали, а не прижимаем его верх к краю.
  // Иначе при широком диапазоне (напр. бас/барабаны внизу) низкие ноты
  // оказываются за нижней кромкой и их не видно.
  const rangeTopY = (127 - range.max) * pxPerKey;
  const rangeBotY = (127 - range.min) * pxPerKey;
  const rangeCenterY = (rangeTopY + rangeBotY) / 2;
  const scrollYPx = clamp(
    rangeCenterY - viewport.h / 2,
    0,
    Math.max(0, totalYPx - viewport.h)
  );
  return { pxPerBeat, pxPerKey, scrollXPx: 0, scrollYPx };
};

const snap = (tick: number, ppq: number, denominator: number | null): number => {
  if (!denominator || denominator <= 0) return Math.max(0, Math.round(tick));
  const ticksPerUnit = (ppq * 4) / denominator;
  return Math.max(0, Math.round(tick / ticksPerUnit) * ticksPerUnit);
};

export const PianoRoll = ({
  midi,
  width,
  height,
  mode = "view",
  playheadTick,
  onSeek,
  enabledChannels,
  markerStripHeight = DEFAULT_MARKER_STRIP_H,
  markers = [],
  onAddMarker,
  onMoveMarker,
  onRenameMarker,
  onDeleteMarker,
  onAddNote,
  onMoveNote,
  onResizeNote,
  onEraseNote,
  snapDenominator = null,
  tool = "select",
}: PianoRollProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const canvasWrapRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rulerRef = useRef<HTMLCanvasElement | null>(null);
  const keyRailRef = useRef<HTMLCanvasElement | null>(null);
  const markerRef = useRef<HTMLCanvasElement | null>(null);
  const markerWrapRef = useRef<HTMLDivElement | null>(null);
  // Hover обновляется через ref + DOM ref напрямую, чтобы не дёргать
  // React-реконсиляцию на каждый pointermove (60–120 Hz).
  const hoverRef = useRef<HTMLDivElement | null>(null);
  const [selection, setSelection] = useState<{ startTick: number; endTick: number } | null>(null);
  const selectionRef = useRef<{ startTick: number; endTick: number } | null>(null);
  const lastPointerTickRef = useRef<number>(0);
  const spaceRef = useRef(false);
  const manualOverrideRef = useRef(false);
  const dragRef = useRef<
    | null
    | { kind: "select"; startTick: number }
    | { kind: "draw"; note: Omit<MidiNote, "channel"> }
    | { kind: "move"; noteId: number; offsetTicks: number; offsetMidi: number }
    | { kind: "resize"; noteId: number; startDuration: number; startTick: number }
  >(null);
  const panRef = useRef<{
    startClientX: number;
    startClientY: number;
    startScrollX: number;
    startScrollY: number;
  } | null>(null);
  const panMovedRef = useRef(false);
  // Маркер-драг: при перетаскивании id/номер такта держим в ref, а текущий
  // measure обновляем live через onMoveMarker. `activeMarkerId` — для подсветки.
  const markerDragRef = useRef<{ id: string } | null>(null);
  const markerMeasureRef = useRef<number | null>(null);
  const [activeMarkerId, setActiveMarkerId] = useState<string | null>(null);
  const [editingMarkerId, setEditingMarkerId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");

  const thumbDragRef = useRef<null | { axis: "x" | "y"; startClient: number; startScroll: number }>(
    null
  );
  // В браузере на одно физическое нажатие срабатывают pointer-событие, а затем
  // совместимое mouse-событие. Чтобы не выполнять side-effect-хэндлеры дважды,
  // подавляем mouse-событие, следующее сразу за pointer-событием. В jsdom-тестах
  // генерируются только mouse-события — флаг не выставляется, они выполняются.
  const suppressMouseRef = useRef(false);

  const mainBackingRef = useRef<{ w: number; h: number; dpr: number } | null>(null);
  const rulerBackingRef = useRef<{ w: number; h: number; dpr: number } | null>(null);
  const keyRailBackingRef = useRef<{ w: number; h: number; dpr: number } | null>(null);
  const markerBackingRef = useRef<{ w: number; h: number; dpr: number } | null>(null);
  const vGridRef = useRef<VGridCache>(createVGridCache());

  const ppq = midi.header.ppq || 480;

  // В editor-режиме паттерн однотрековый (sliceMidi), агрегация не нужна —
  // редактирование индексируется по midi.tracks[0].notes (noteId = idx).
  // В view-режиме ноты всех треков агрегируются (с учётом enabledChannels)
  // для отрисовки и расчёта pitchRange.
  const displayNotes = useMemo<DisplayNote[]>(() => {
    if (mode === "editor") {
      const t = midi.tracks[0];
      if (!t) return [];
      return t.notes.map((n) => ({ note: n, channel: n.channel ?? t.channel }));
    }
    const out: DisplayNote[] = [];
    for (const t of midi.tracks) {
      for (const n of t.notes) {
        const channel = n.channel ?? t.channel;
        if (enabledChannels && !enabledChannels.has(channel)) continue;
        out.push({ note: n, channel });
      }
    }
    return out;
  }, [mode, midi, enabledChannels]);

  const timeSigs: ReadonlyArray<MidiTimeSignature> = midi.header.timeSignatures;

  // Для каллинга нот при отрисовке: массив, отсортированный по ticks, чтобы
  // бинарным поиском ограничить цикл видимым окном (O(log n + видимые) вместо
  // O(n) на кадр). findNoteAt продолжает работать по displayNotes (исходный
  // порядок, нужный для noteId в editor-режиме).
  const sortedNotes = useMemo(
    () =>
      [...displayNotes].sort(
        (a, b) => a.note.ticks - b.note.ticks || a.note.midi - b.note.midi
      ),
    [displayNotes]
  );
  const maxNoteDuration = useMemo(() => {
    let max = 0;
    for (const d of displayNotes) {
      if (d.note.durationTicks > max) max = d.note.durationTicks;
    }
    return max;
  }, [displayNotes]);

  // Полная длина песни (по всем трекам, БЕЗ учёта фильтра каналов) — из неё
  // считаются границы сетки/линейки/полоски меток. Метки оперируют мерами
  // относительно полного файла (см. App.tsx), поэтому границы не должны
  // сжиматься, когда канал с последними нотами выключен — иначе метки в
  // хвосте песни скроются/съедут. Фильтр `enabledChannels` применяется
  // только к отрисовке/каллингу нот.
  const songEndTick = useMemo(() => {
    let max = 0;
    for (const t of midi.tracks) {
      for (const n of t.notes) max = Math.max(max, n.ticks + n.durationTicks);
    }
    return Math.max(max, ppq * 4);
  }, [midi, ppq]);

  const boundaries = useMemo(
    () => measureBoundaries(ppq, timeSigs, songEndTick),
    [ppq, timeSigs, songEndTick]
  );

  // Вьюпорт: ResizeObserver на canvasWrap (ширина+высота). Fallback —
  // пропсы width/height (необходим в jsdom, где RO отсутствует).
  const [viewport, setViewport] = useState<{ w: number; h: number }>({ w: width, h: height });
  const viewportRef = useRef<{ w: number; h: number }>({ w: width, h: height });

  const initialView = useMemo<ViewState>(
    () => computeFit({ w: width, h: height }, boundaries, ppq, displayNotes),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const [viewState, setViewState] = useState<ViewState>(initialView);
  const viewStateRef = useRef<ViewState>(initialView);

  const boundariesRef = useRef(boundaries);
  const displayNotesRef = useRef(displayNotes);
  const ppqRef = useRef(ppq);
  const playheadRef = useRef(playheadTick);

  useEffect(() => {
    viewportRef.current = viewport;
  }, [viewport]);
  useEffect(() => {
    viewStateRef.current = viewState;
  }, [viewState]);
  useEffect(() => {
    boundariesRef.current = boundaries;
  }, [boundaries]);
  useEffect(() => {
    displayNotesRef.current = displayNotes;
  }, [displayNotes]);
  useEffect(() => {
    ppqRef.current = ppq;
  }, [ppq]);
  useEffect(() => {
    playheadRef.current = playheadTick;
  }, [playheadTick]);

  const applyViewState = useCallback((updater: (v: ViewState) => ViewState) => {
    const next = clampViewState(
      updater(viewStateRef.current),
      viewportRef.current,
      boundariesRef.current,
      ppqRef.current
    );
    viewStateRef.current = next;
    setViewState(next);
  }, []);

  const fitView = useCallback(() => {
    manualOverrideRef.current = false;
    applyViewState(() =>
      computeFit(viewportRef.current, boundariesRef.current, ppqRef.current, displayNotesRef.current)
    );
  }, [applyViewState]);

  const fitRef = useRef(fitView);
  const applyViewStateRef = useRef(applyViewState);
  useEffect(() => {
    fitRef.current = fitView;
    applyViewStateRef.current = applyViewState;
  }, [fitView, applyViewState]);

  // Наблюдатель за размером вьюпорта.
  useEffect(() => {
    const wrap = canvasWrapRef.current;
    if (!wrap || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => {
      let w = 0;
      let h = 0;
      for (const e of entries) {
        if (e.target === wrap) {
          w = e.contentRect.width;
          h = e.contentRect.height;
        }
      }
      if (w <= 0 || h <= 0) return;
      const prev = viewportRef.current;
      viewportRef.current = { w, h };
      if (prev.w !== w || prev.h !== h) {
        setViewport({ w, h });
        if (!manualOverrideRef.current) fitRef.current();
        else applyViewStateRef.current((v) => v);
      }
    });
    ro.observe(wrap);
    return () => ro.disconnect();
  }, []);

  // Content-ключ: сбрасывает вьюпорт к fit только при смене структуры песни
  // (новый файл / иной размер), а НЕ при переключении каналов. Границы сетки
  // считаются от полного файла (`songEndTick`), поэтому выключение канала не
  // сжимает контент — масштаб и позиция должны сохраняться при фильтрации.
  // totalTicks (отфильтрованный) и enabledChannels намеренно НЕ входят в ключ.
  // В editor-режиме не трогаем — иначе рефит при каждом add/move сломал бы
  // скролл во время драга.
  const contentKey = useMemo(
    () =>
      `${midi.header.ppq}|${midi.tracks.length}|${songEndTick}|${timeSigs
        .map((s) => `${s.ticks}:${s.timeSignature[0]}/${s.timeSignature[1]}`)
        .join(",")}`,
    [midi.header.ppq, midi.tracks.length, songEndTick, timeSigs]
  );
  const lastFitKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (mode !== "view") return;
    if (lastFitKeyRef.current !== contentKey) {
      lastFitKeyRef.current = contentKey;
      fitRef.current();
    }
  }, [contentKey, mode]);

  const drawMain = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = resizeCanvas(canvas, viewport.w, viewport.h, mainBackingRef);
    if (!ctx) return;
    const colors = PIANO_ROLL_COLORS;
    const vs = viewState;
    const pxPerTick = vs.pxPerBeat / ppq;
    const pxPerKey = vs.pxPerKey;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, viewport.w, viewport.h);
    ctx.translate(-vs.scrollXPx, -vs.scrollYPx);

    const x0 = vs.scrollXPx;
    const x1 = vs.scrollXPx + viewport.w;
    const y0 = vs.scrollYPx;
    const y1 = vs.scrollYPx + viewport.h;
    const padY = PAD_OCTAVES * 12 * pxPerKey;
    const padX = ppq * pxPerTick * PAD_MEASURES;

    // Ноты рисуем первыми (поверх однородного фона), а сетку — ПОВЕРХ нот.
    // Плотные участки (много коротких нот на такт) тогда не превращаются в
    // сплошную цветную «стенку»-фон: тактовые/долевые линии всегда видны и
    // отделяют ноты. Непрозрачность нот поднята, чтобы тихие ноты не были
    // почти невидимы.
    const pad = Math.max(padX, padY);
    const cullStart = (x0 - pad) / pxPerTick;
    const cullEnd = (x1 + pad) / pxPerTick;
    const loIdx = lowerBound(sortedNotes, cullStart - maxNoteDuration, (d) => d.note.ticks);
    for (let i = loIdx; i < sortedNotes.length; i += 1) {
      const d = sortedNotes[i]!;
      const n = d.note;
      if (n.ticks > cullEnd) break;
      const nx = n.ticks * pxPerTick;
      const ny = (127 - n.midi) * pxPerKey;
      if (ny + pxPerKey < y0 - pad || ny > y1 + pad) continue;
      const nw = Math.max(2, n.durationTicks * pxPerTick);
      const alpha = 0.6 + 0.4 * n.velocity;
      const isDrum = d.channel === 9;
      ctx.globalAlpha = alpha;
      ctx.fillStyle = isDrum ? DRUM_COLOR : channelColor(d.channel);
      ctx.fillRect(nx, ny + 1, nw, Math.max(2, pxPerKey - 2));
      ctx.globalAlpha = 1;
    }

    // Горизонтальные ориентиры питча: октавные линии C (поверх нот) — батчем.
    const topM = clamp(127 - Math.floor((y0 - padY) / pxPerKey), 0, 127);
    const bottomM = clamp(127 - Math.ceil((y1 + padY) / pxPerKey), 0, 127);
    const octYs: number[] = [];
    for (let m = topM; m >= bottomM; m -= 1) {
      if (m % 12 === 0) octYs.push((127 - m) * pxPerKey);
    }
    strokeHGroup(ctx, octYs, x0, x1, colors.gridStrong, 1);

    // Вертикальная сетка: такты / доли / 1/8 / 1/16 (поверх нот). Фаза 2:
    // рисуется в offscreen-канвас при изменении зума/контента/скролла за край
    // кэша, при скролле в пределах кэша — drawImage со сдвигом. Фоллбэк (нет
    // OffscreenCanvas) — прямое батченное рисование каждый кадр.
    const gridColors = {
      strong: colors.gridStrong,
      med: colors.gridMed,
      faint: colors.gridFaint,
      sub: colors.gridSub,
    };
    const gridLevels: TimeGridLevels = {
      eighths: true,
      eighthsMinBeatPx: 8, // 1/8 показываются с beatPx/2 >= 4 (как раньше)
      sixteenths: true,
      sixteenthsMinBeatPx: 20, // 1/16 — с beatPx/4 >= 5
    };
    const gridDpr = mainBackingRef.current?.dpr ?? 1;
    const gridCacheCtx = ensureVGridCache(
      vGridRef.current,
      viewport.w,
      viewport.h,
      gridDpr,
      GRID_CACHE_MARGIN
    );
    if (gridCacheCtx) {
      const gridKey = `${pxPerTick}|${ppq}|${contentKey}`;
      if (vGridCacheNeedsRender(vGridRef.current, gridKey, vs.scrollXPx)) {
        renderVGridCache(
          vGridRef.current,
          viewport.w,
          viewport.h,
          gridDpr,
          GRID_CACHE_MARGIN,
          vs.scrollXPx,
          boundaries,
          timeSigs,
          ppq,
          pxPerTick,
          gridLevels,
          gridColors
        );
        vGridRef.current.key = gridKey;
      }
      blitVGridCache(ctx, vGridRef.current, vs.scrollYPx);
    } else {
      renderTimeGrid(
        ctx,
        boundaries,
        timeSigs,
        ppq,
        pxPerTick,
        x0 - padX,
        x1 + padX,
        y0,
        y1,
        gridLevels,
        gridColors
      );
    }

    // Выделение (editor-режим).
    if (selection) {
      const sx1 = selection.startTick * pxPerTick;
      const sx2 = selection.endTick * pxPerTick;
      ctx.fillStyle = "rgba(91, 140, 255, 0.15)";
      ctx.fillRect(Math.min(sx1, sx2), y0, Math.abs(sx2 - sx1), viewport.h);
      ctx.strokeStyle = "rgba(91, 140, 255, 0.7)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(sx1, y0);
      ctx.lineTo(sx1, y1);
      ctx.moveTo(sx2, y0);
      ctx.lineTo(sx2, y1);
      ctx.stroke();
    }

    // Playhead.
    if (typeof playheadTick === "number" && playheadTick >= 0) {
      const px = playheadTick * pxPerTick;
      ctx.strokeStyle = "rgba(20, 30, 60, 0.85)";
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(px, y0);
      ctx.lineTo(px, y1);
      ctx.stroke();
    }
  }, [
    boundaries,
    contentKey,
    maxNoteDuration,
    playheadTick,
    ppq,
    selection,
    sortedNotes,
    timeSigs,
    viewState,
    viewport,
  ]);

  const drawRuler = useCallback(() => {
    const canvas = rulerRef.current;
    if (!canvas) return;
    const ctx = resizeCanvas(canvas, viewport.w, RULER_H, rulerBackingRef);
    if (!ctx) return;
    const colors = PIANO_ROLL_COLORS;
    const vs = viewState;
    const pxPerTick = vs.pxPerBeat / ppq;
    ctx.fillStyle = "var(--surface-2)";
    ctx.fillRect(0, 0, viewport.w, RULER_H);
    ctx.translate(-vs.scrollXPx, 0);
    const x0 = vs.scrollXPx - 60;
    const x1 = vs.scrollXPx + viewport.w + 60;
    // Тактовые метки и доли — батчами (по одному stroke на стиль); старт
    // цикла через бинарный поиск.
    const startIdx = lowerBound(boundaries, x0 / pxPerTick, (v) => v);
    const measureXs: number[] = [];
    const measureIdxs: number[] = [];
    const beatXs: number[] = [];
    for (let i = startIdx; i < boundaries.length; i += 1) {
      const mStart = boundaries[i]!;
      const mx = mStart * pxPerTick;
      if (mx > x1) break;
      measureXs.push(mx);
      measureIdxs.push(i);
      if (i + 1 < boundaries.length) {
        const mEnd = boundaries[i + 1]!;
        const sig = signatureAt(timeSigs, mStart);
        const tpb = (4 / sig[1]) * ppq;
        for (let t = mStart + tpb; t < mEnd; t += tpb) {
          const bx = t * pxPerTick;
          if (bx >= x0 && bx <= x1) beatXs.push(bx);
        }
      }
    }
    strokeVGroup(ctx, measureXs, RULER_H - 6, RULER_H, colors.gridStrong, 1);
    strokeVGroup(ctx, beatXs, RULER_H - 3, RULER_H, colors.gridMed, 1);
    ctx.fillStyle = colors.keyLabel;
    ctx.font = "9px ui-monospace, monospace";
    for (let k = 0; k < measureXs.length; k += 1) {
      ctx.fillText("M" + (measureIdxs[k]! + 1), measureXs[k]! + 3, 11);
    }
  }, [boundaries, ppq, timeSigs, viewState, viewport]);

  const drawKeyRail = useCallback(() => {
    const canvas = keyRailRef.current;
    if (!canvas) return;
    // Зона над нотной областью (canvasWrap): в view-режиме это полоска меток
    // + линейка, в editor — только линейка. Клавиши должны начинаться ровно
    // с верха нотной области, иначе из-за добавленной полоски меток они
    // съезжают вверх относительно сетки/нот на её высоту.
    const topPad = (mode === "view" ? markerStripHeight : 0) + RULER_H;
    const railH = viewport.h + topPad;
    const ctx = resizeCanvas(canvas, KEY_RAIL_W, railH, keyRailBackingRef);
    if (!ctx) return;
    const colors = PIANO_ROLL_COLORS;
    const vs = viewState;
    const pxPerKey = vs.pxPerKey;
    ctx.fillStyle = "var(--surface-2)";
    ctx.fillRect(0, 0, KEY_RAIL_W, railH);
    ctx.translate(0, topPad - vs.scrollYPx);
    const y0 = vs.scrollYPx;
    const y1 = vs.scrollYPx + viewport.h;
    const pad = PAD_OCTAVES * 12 * pxPerKey;
    const topM = clamp(127 - Math.floor((y0 - pad) / pxPerKey), 0, 127);
    const bottomM = clamp(127 - Math.ceil((y1 + pad) / pxPerKey), 0, 127);
    const edgeYs: number[] = [];
    for (let m = topM; m >= bottomM; m -= 1) {
      const y = (127 - m) * pxPerKey;
      ctx.fillStyle = BLACK_KEYS.has(m % 12) ? colors.keyBlack : colors.keyWhite;
      ctx.fillRect(0, y, KEY_RAIL_W, pxPerKey);
      edgeYs.push(y + pxPerKey);
    }
    strokeHGroup(ctx, edgeYs, 0, KEY_RAIL_W, colors.keyBorder, 1);
    ctx.fillStyle = colors.keyLabel;
    ctx.font = "9px ui-monospace, monospace";
    for (let m = topM; m >= bottomM; m -= 1) {
      if (m % 12 === 0) {
        const y = (127 - m) * pxPerKey;
        ctx.fillText("C" + (Math.floor(m / 12) - 1), 4, y + pxPerKey - 2);
      }
    }
    ctx.strokeStyle = colors.keyBorder;
    ctx.beginPath();
    ctx.moveTo(KEY_RAIL_W - 0.5, y0);
    ctx.lineTo(KEY_RAIL_W - 0.5, y1);
    ctx.stroke();
  }, [markerStripHeight, mode, viewState, viewport]);

  // Фон полоски меток: вертикальные линии тактов (без подписей — подписи
  // рисуют DOM-чипы, интерактивные).
  const drawMarkerStrip = useCallback(() => {
    const canvas = markerRef.current;
    if (!canvas) return;
    const ctx = resizeCanvas(canvas, viewport.w, markerStripHeight, markerBackingRef);
    if (!ctx) return;
    const colors = PIANO_ROLL_COLORS;
    const vs = viewState;
    const pxPerTick = vs.pxPerBeat / ppq;
    ctx.fillStyle = "var(--surface-2)";
    ctx.fillRect(0, 0, viewport.w, markerStripHeight);
    ctx.translate(-vs.scrollXPx, 0);
    const x0 = vs.scrollXPx - 60;
    const x1 = vs.scrollXPx + viewport.w + 60;
    const xs: number[] = [];
    const startIdx = lowerBound(boundaries, x0 / pxPerTick, (v) => v);
    for (let i = startIdx; i < boundaries.length; i += 1) {
      const mx = boundaries[i]! * pxPerTick;
      if (mx > x1) break;
      xs.push(mx);
    }
    strokeVGroup(ctx, xs, 0, markerStripHeight, colors.gridFaint, 1);
  }, [boundaries, markerStripHeight, ppq, viewState, viewport]);

  // Автоподскролл playhead в rAF-цикле (мягкий сдвиг с порогом ~1.5 доли).
  // Следуем ТОЛЬКО вперёд: когда playhead пересёк правый край вьюпорта —
  // чтобы не «выдёргивать» вьюпорт назад, если пользователь вручную
  // проскроллил вперёд, а playhead остался слева.
  const autofollow = useCallback(() => {
    if (mode !== "view") return;
    const tick = playheadRef.current;
    if (typeof tick !== "number" || tick < 0) return;
    const vs = viewStateRef.current;
    const vp = viewportRef.current;
    const pxPerTick = vs.pxPerBeat / ppqRef.current;
    const x = tick * pxPerTick;
    const bounds = boundariesRef.current;
    const totalPx = (bounds[bounds.length - 1] ?? 0) * pxPerTick;
    const maxScrollX = Math.max(0, totalPx - vp.w);
    const pad = vs.pxPerBeat * 1.5;
    if (x > vs.scrollXPx + vp.w - pad) {
      const next = clamp(x - vp.w / 3, 0, maxScrollX);
      if (Math.abs(next - vs.scrollXPx) > 0.5) {
        applyViewState((v) => ({ ...v, scrollXPx: next }));
      }
    }
  }, [applyViewState, mode]);

  const autofollowRef = useRef(autofollow);
  useEffect(() => {
    autofollowRef.current = autofollow;
  }, [autofollow]);

  // Автоподскролл дополнительно срабатывает на каждом изменении playhead
  // (надёжно и без rAF — важно для jsdom-тестов и стабильности).
  useEffect(() => {
    if (mode !== "view") return;
    if (typeof playheadTick === "number" && playheadTick >= 0) {
      autofollowRef.current();
    }
  }, [playheadTick, mode]);

  const drawFrame = useCallback(() => {
    const t0 = IS_DEV && typeof performance !== "undefined" ? performance.now() : 0;
    autofollowRef.current();
    drawMain();
    drawRuler();
    drawKeyRail();
    drawMarkerStrip();
    profileDrawFrame("PianoRoll", t0);
  }, [drawKeyRail, drawMain, drawMarkerStrip, drawRuler]);

  useRafFrame(drawFrame);

  // Подавление совместимых mouse-событий после pointer-событий (см. ref выше).
  const pointerGate = useCallback((type: string): boolean => {
    if (type.startsWith("pointer")) {
      suppressMouseRef.current = true;
      return false;
    }
    if (suppressMouseRef.current) {
      suppressMouseRef.current = false;
      return true;
    }
    return false;
  }, []);

  // Один getBoundingClientRect на событие: и tick, и midi считаются от него.
  const localFromClient = useCallback(
    (clientX: number, clientY: number): { tick: number; midi: number } => {
      const canvas = canvasRef.current;
      const vs = viewStateRef.current;
      if (!canvas) return { tick: 0, midi: 60 };
      const rect = canvas.getBoundingClientRect();
      const pxPerTick = vs.pxPerBeat / ppqRef.current;
      const x = clientX - rect.left;
      const y = clientY - rect.top;
      const tick = Math.max(0, Math.round((vs.scrollXPx + x) / pxPerTick));
      const midi = Math.max(0, Math.min(127, 127 - Math.floor((vs.scrollYPx + y) / vs.pxPerKey)));
      return { tick: snap(tick, ppqRef.current, snapDenominator), midi };
    },
    [snapDenominator]
  );

  // Преобразование координаты курсора в тик на полоске меток.
  const tickAtMarkerClientX = useCallback(
    (clientX: number): number => {
      const wrap = markerWrapRef.current;
      const vs = viewStateRef.current;
      if (!wrap) return 0;
      const rect = wrap.getBoundingClientRect();
      const pxPerTick = vs.pxPerBeat / ppqRef.current;
      const x = clientX - rect.left;
      return Math.max(0, Math.round((vs.scrollXPx + x) / pxPerTick));
    },
    []
  );

  // Индекс границы (меры), содержащей tick. Вернёт индекс в `boundaries`.
  const measureIndexAtTick = useCallback((tick: number): number => {
    const b = boundariesRef.current;
    if (b.length === 0) return 0;
    for (let i = b.length - 1; i >= 0; i -= 1) {
      if (b[i]! <= tick) return i;
    }
    return 0;
  }, []);

  const findNoteAt = useCallback(
    (tick: number, midi: number): { idx: number; note: MidiNote } | null => {
      for (let i = 0; i < displayNotes.length; i += 1) {
        const n = displayNotes[i]!.note;
        if (n.midi === midi && tick >= n.ticks && tick < n.ticks + n.durationTicks) {
          return { idx: i, note: n };
        }
      }
      return null;
    },
    [displayNotes]
  );

  const zoomAt = useCallback(
    (cx: number, cy: number, factor: number) => {
      manualOverrideRef.current = true;
      const vs = viewStateRef.current;
      const vp = viewportRef.current;
      const pxPerTick = vs.pxPerBeat / ppqRef.current;
      const tickAnchor = (vs.scrollXPx + cx) / pxPerTick;
      const midiAnchor = 127 - (vs.scrollYPx + cy) / vs.pxPerKey;
      const nextPxPerBeat = clamp(vs.pxPerBeat * factor, MIN_PX_PER_BEAT, MAX_PX_PER_BEAT);
      const nextPxPerKey = clamp(vs.pxPerKey * factor, MIN_PX_PER_KEY, MAX_PX_PER_KEY);
      const nextTick = nextPxPerBeat / ppqRef.current;
      const nextScrollX = tickAnchor * nextTick - cx;
      const nextScrollY = (127 - midiAnchor) * nextPxPerKey - cy;
      applyViewState((v) => ({
        pxPerBeat: nextPxPerBeat,
        pxPerKey: nextPxPerKey,
        scrollXPx: nextScrollX,
        scrollYPx: nextScrollY,
      }));
    },
    [applyViewState]
  );

  const zoomAtRef = useRef(zoomAt);
  useEffect(() => {
    zoomAtRef.current = zoomAt;
  }, [zoomAt]);

  const scrollBy = useCallback(
    (delta: number, vertical: boolean) => {
      manualOverrideRef.current = true;
      applyViewState((v) => {
        if (vertical) return { ...v, scrollYPx: v.scrollYPx + delta };
        return { ...v, scrollXPx: v.scrollXPx + delta };
      });
    },
    [applyViewState]
  );

  const scrollByRef = useRef(scrollBy);
  useEffect(() => {
    scrollByRef.current = scrollBy;
  }, [scrollBy]);

  // Wheel — нативный listener с { passive: false }, иначе preventDefault
  // не сработает (React onWheel — passive). preventDefault вызываем только
  // когда пиано-ролл реально потребляет событие (соответствующая ось
  // переполнена), иначе скролл страницы/зум под канвасом продолжает работать.
  // Shift/Ctrl/Cmd+wheel = зум; trackpad deltaX = горизонтальный скролл;
  // обычное колесо = вертикальный скролл.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const onWheel = (e: WheelEvent) => {
      if (e.shiftKey || e.ctrlKey || e.metaKey) {
        // Зум вокруг курсора всегда потребляет событие.
        e.preventDefault();
        const rect = canvas.getBoundingClientRect();
        zoomAtRef.current(
          e.clientX - rect.left,
          e.clientY - rect.top,
          Math.exp(-e.deltaY * ZOOM_WHEEL_FACTOR)
        );
        return;
      }
      const vs = viewStateRef.current;
      const vp = viewportRef.current;
      const pxPerTick = vs.pxPerBeat / ppqRef.current;
      const bounds = boundariesRef.current;
      const totalPx = (bounds[bounds.length - 1] ?? 0) * pxPerTick;
      const totalYPx = 128 * vs.pxPerKey;
      const hOverflow = totalPx > vp.w;
      const vOverflow = totalYPx > vp.h;
      if (e.deltaX !== 0 && hOverflow) {
        e.preventDefault();
        scrollByRef.current(e.deltaX, false);
        return;
      }
      if (vOverflow) {
        e.preventDefault();
        scrollByRef.current(e.deltaY, true);
      }
    };
    canvas.addEventListener("wheel", onWheel, { passive: false });
    return () => canvas.removeEventListener("wheel", onWheel);
  }, []);

  const handleZoomIn = useCallback(() => {
    zoomAtRef.current(viewportRef.current.w / 2, viewportRef.current.h / 2, ZOOM_STEP);
  }, []);

  const handleZoomOut = useCallback(() => {
    zoomAtRef.current(viewportRef.current.w / 2, viewportRef.current.h / 2, 1 / ZOOM_STEP);
  }, []);

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (pointerGate(e.type)) return;
    const vs = viewStateRef.current;
    const isPan = e.button === 1 || (e.button === 0 && spaceRef.current);
    if (isPan) {
      e.preventDefault();
      manualOverrideRef.current = true;
      panRef.current = {
        startClientX: e.clientX,
        startClientY: e.clientY,
        startScrollX: vs.scrollXPx,
        startScrollY: vs.scrollYPx,
      };
      if (typeof e.pointerId === "number" && typeof e.currentTarget.setPointerCapture === "function") {
        e.currentTarget.setPointerCapture(e.pointerId);
      }
      return;
    }
    const { tick, midi } = localFromClient(e.clientX, e.clientY);
    // Фиксируем позицию pointerdown, чтобы на клике без движения (down+up без
    // pointermove) tick корректно резолвился при seek.
    lastPointerTickRef.current = tick;
    if (typeof e.pointerId === "number" && typeof e.currentTarget.setPointerCapture === "function") {
      e.currentTarget.setPointerCapture(e.pointerId);
    }
    containerRef.current?.focus({ preventScroll: true });

    if (mode === "editor") {
      if (e.shiftKey || tool === "select") {
        const sel = { startTick: tick, endTick: tick };
        selectionRef.current = sel;
        setSelection(sel);
        dragRef.current = { kind: "select", startTick: tick };
        return;
      }
      if (tool === "erase") {
        const hit = findNoteAt(tick, midi);
        if (hit && onEraseNote) onEraseNote(hit.idx);
        return;
      }
      if (onAddNote) {
        const newNote: Omit<MidiNote, "channel"> = {
          midi,
          ticks: tick,
          durationTicks: snap(ppq, ppq, snapDenominator),
          velocity: 0.8,
        };
        dragRef.current = { kind: "draw", note: newNote };
        onAddNote(newNote);
        return;
      }
      return;
    }

    // view mode: ЛКМ = клик-в-seek или drag-pan (оба через panRef; seek
    // резолвится на pointerup при отсутствии движения).
    manualOverrideRef.current = true;
    panRef.current = {
      startClientX: e.clientX,
      startClientY: e.clientY,
      startScrollX: vs.scrollXPx,
      startScrollY: vs.scrollYPx,
    };
    panMovedRef.current = false;
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (pointerGate(e.type)) return;
    const pan = panRef.current;
    if (pan) {
      const dx = e.clientX - pan.startClientX;
      const dy = e.clientY - pan.startClientY;
      if (!panMovedRef.current && (Math.abs(dx) > PAN_THRESHOLD || Math.abs(dy) > PAN_THRESHOLD)) {
        panMovedRef.current = true;
      }
      if (panMovedRef.current) {
        applyViewState((v) => ({
          ...v,
          scrollXPx: pan.startScrollX - dx,
          scrollYPx: pan.startScrollY - dy,
        }));
      }
      return;
    }
    const { tick, midi } = localFromClient(e.clientX, e.clientY);
    lastPointerTickRef.current = tick;
    if (hoverRef.current) {
      hoverRef.current.textContent =
        "midi=" + String(midi) + " · t=" + String(tick);
    }
    const drag = dragRef.current;
    if (!drag) return;
    if (drag.kind === "select") {
      const start = Math.min(drag.startTick, tick);
      const end = Math.max(drag.startTick, tick);
      const sel = { startTick: start, endTick: end };
      selectionRef.current = sel;
      setSelection(sel);
      return;
    }
    if (drag.kind === "draw" && onAddNote) {
      const dur = Math.max(1, tick - drag.note.ticks);
      onAddNote({ ...drag.note, durationTicks: snap(dur, ppq, snapDenominator) });
      return;
    }
    if (drag.kind === "move" && onMoveNote) {
      const newTick = Math.max(0, tick - drag.offsetTicks);
      const newMidi = Math.max(0, Math.min(127, midi - drag.offsetMidi));
      onMoveNote(drag.noteId, snap(newTick, ppq, snapDenominator), newMidi);
      return;
    }
    if (drag.kind === "resize" && onResizeNote) {
      const dur = Math.max(1, tick - drag.startTick);
      onResizeNote(drag.noteId, snap(dur, ppq, snapDenominator));
      return;
    }
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (pointerGate(e.type)) return;
    if (typeof e.pointerId === "number" && typeof e.currentTarget.releasePointerCapture === "function") {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    if (panRef.current) {
      if (!panMovedRef.current) {
        // Клик без движения → seek.
        const { tick } = localFromClient(e.clientX, e.clientY);
        if (onSeek) onSeek(tick);
      }
      panRef.current = null;
      panMovedRef.current = false;
      return;
    }
    const drag = dragRef.current;
    if (drag?.kind === "select") {
      const endTick = lastPointerTickRef.current;
      const sel = {
        startTick: Math.min(drag.startTick, endTick),
        endTick: Math.max(drag.startTick, endTick),
      };
      selectionRef.current = sel;
      if (sel.endTick <= sel.startTick) {
        selectionRef.current = null;
        setSelection(null);
      }
    }
    dragRef.current = null;
  };

  const handlePointerLeave = () => {
    suppressMouseRef.current = false;
    if (hoverRef.current) hoverRef.current.textContent = "";
  };

  // --- Полоска меток: интерактивные DOM-чипы над canvas-фоном ---

  const startMarkerDrag = (e: React.PointerEvent<HTMLElement>, id: string) => {
    if (pointerGate(e.type)) return;
    e.preventDefault();
    e.stopPropagation();
    setEditingMarkerId(null);
    markerDragRef.current = { id };
    setActiveMarkerId(id);
    if (typeof e.pointerId === "number" && typeof e.currentTarget.setPointerCapture === "function") {
      e.currentTarget.setPointerCapture(e.pointerId);
    }
  };

  const moveMarkerDrag = (e: React.PointerEvent<HTMLElement>) => {
    if (pointerGate(e.type)) return;
    const drag = markerDragRef.current;
    if (!drag) return;
    const tick = tickAtMarkerClientX(e.clientX);
    const measure = measureIndexAtTick(tick);
    if (markerMeasureRef.current !== measure) {
      markerMeasureRef.current = measure;
      const b = boundariesRef.current;
      onMoveMarker?.(drag.id, b[measure]!);
    }
  };

  const endMarkerDrag = (e: React.PointerEvent<HTMLElement>) => {
    if (pointerGate(e.type)) return;
    if (typeof e.pointerId === "number" && typeof e.currentTarget.releasePointerCapture === "function") {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    markerDragRef.current = null;
    markerMeasureRef.current = null;
    setActiveMarkerId(null);
  };

  const handleMarkerWrapPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (pointerGate(e.type)) return;
    // Пустое место (вне чипа) → добавить метку с snap к такту.
    const tick = tickAtMarkerClientX(e.clientX);
    const measure = measureIndexAtTick(tick);
    const b = boundariesRef.current;
    onAddMarker?.(b[measure]!);
  };

  const handleMarkerWrapDoubleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Двойной клик по чипу (уже делегирован) — переименовать.
    const wrap = markerWrapRef.current;
    if (!wrap) return;
    const rect = wrap.getBoundingClientRect();
    const x = e.clientX - rect.left;
    for (const m of markers) {
      const mTick = (boundaries[m.measure - 1] ?? 0) * (viewState.pxPerBeat / ppq);
      const mx = mTick - viewState.scrollXPx;
      if (Math.abs(x - mx) < 10) {
        setEditingMarkerId(m.id);
        setEditingName(m.name);
        return;
      }
    }
  };

  const commitRename = () => {
    if (editingMarkerId) {
      onRenameMarker?.(editingMarkerId, editingName);
      setEditingMarkerId(null);
      setEditingName("");
    }
  };

  // Позиции DOM-чипов меток (только видимые в вьюпорте). Зависит только от
  // горизонтальной геометрии — вертикальный скролл/зум не двигает чипы,
  // поэтому не должен их пересчитывать (чипы рендерятся с ключами `id`,
  // React при скролле только патчит style.left у существующих узлов).
  const markerChips = useMemo(() => {
    const pxPerTick = viewState.pxPerBeat / ppq;
    const out: Array<{ id: string; measure: number; name: string; left: number }> = [];
    for (const m of markers) {
      const tick = boundaries[m.measure - 1];
      if (tick === undefined) continue;
      const mx = tick * pxPerTick - viewState.scrollXPx;
      if (mx < -60 || mx > viewport.w + 60) continue;
      out.push({ id: m.id, measure: m.measure, name: m.name, left: mx });
    }
    return out;
  }, [boundaries, markers, ppq, viewState.pxPerBeat, viewState.scrollXPx, viewport.w]);

  // Позиция input для переименования (слева от чипа активной метки).
  const editingX = useMemo(() => {
    if (!editingMarkerId) return null;
    const m = markers.find((x) => x.id === editingMarkerId);
    if (!m) return null;
    const tick = boundaries[m.measure - 1];
    if (tick === undefined) return null;
    return tick * (viewState.pxPerBeat / ppq) - viewState.scrollXPx;
  }, [boundaries, editingMarkerId, markers, ppq, viewState]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === " " || e.key === "Spacebar") {
      e.preventDefault();
      spaceRef.current = true;
      return;
    }
    const k = e.key;
    if (k === "+" || k === "=") {
      e.preventDefault();
      handleZoomIn();
    } else if (k === "-") {
      e.preventDefault();
      handleZoomOut();
    } else if (k === "0") {
      e.preventDefault();
      fitRef.current();
    } else if (k === "ArrowLeft") {
      e.preventDefault();
      scrollByRef.current(-40, false);
    } else if (k === "ArrowRight") {
      e.preventDefault();
      scrollByRef.current(40, false);
    } else if (k === "ArrowUp") {
      e.preventDefault();
      scrollByRef.current(-40, true);
    } else if (k === "ArrowDown") {
      e.preventDefault();
      scrollByRef.current(40, true);
    }
  };

  const handleKeyUp = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === " " || e.key === "Spacebar") {
      e.preventDefault();
      spaceRef.current = false;
    }
  };

  const handleBlur = () => {
    spaceRef.current = false;
  };

  const startThumbDrag = (e: React.PointerEvent<HTMLDivElement>, axis: "x" | "y") => {
    if (pointerGate(e.type)) return;
    e.preventDefault();
    const vs = viewStateRef.current;
    if (typeof e.pointerId === "number" && typeof e.currentTarget.setPointerCapture === "function") {
      e.currentTarget.setPointerCapture(e.pointerId);
    }
    thumbDragRef.current = {
      axis,
      startClient: axis === "x" ? e.clientX : e.clientY,
      startScroll: axis === "x" ? vs.scrollXPx : vs.scrollYPx,
    };
  };

  const moveThumb = (e: React.PointerEvent<HTMLDivElement>) => {
    if (pointerGate(e.type)) return;
    const td = thumbDragRef.current;
    if (!td) return;
    const vs = viewStateRef.current;
    const vp = viewportRef.current;
    const cur = td.axis === "x" ? e.clientX : e.clientY;
    const delta = cur - td.startClient;
    manualOverrideRef.current = true;
    if (td.axis === "x") {
      const pxPerTick = vs.pxPerBeat / ppqRef.current;
      const bounds = boundariesRef.current;
      const totalPx = (bounds[bounds.length - 1] ?? 0) * pxPerTick;
      const ratio = totalPx / Math.max(1, vp.w);
      applyViewState((v) => ({ ...v, scrollXPx: td.startScroll + delta * ratio }));
    } else {
      const ratio = (128 * vs.pxPerKey) / Math.max(1, vp.h);
      applyViewState((v) => ({ ...v, scrollYPx: td.startScroll + delta * ratio }));
    }
  };

  const endThumb = (e: React.PointerEvent<HTMLDivElement>) => {
    if (pointerGate(e.type)) return;
    if (typeof e.pointerId === "number" && typeof e.currentTarget.releasePointerCapture === "function") {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    thumbDragRef.current = null;
  };

  const pxPerTick = viewState.pxPerBeat / ppq;
  const totalPx = (boundaries[boundaries.length - 1] ?? 0) * pxPerTick;
  const totalYPx = 128 * viewState.pxPerKey;
  const hVisible = totalPx > viewport.w;
  const vVisible = totalYPx > viewport.h;
  const thumbW = hVisible ? Math.max(SCROLLBAR_MIN, (viewport.w / totalPx) * viewport.w) : 0;
  const thumbX = hVisible ? (viewState.scrollXPx / totalPx) * viewport.w : 0;
  const thumbH = vVisible ? Math.max(SCROLLBAR_MIN, (viewport.h / totalYPx) * viewport.h) : 0;
  const thumbY = vVisible ? (viewState.scrollYPx / totalYPx) * viewport.h : 0;

  const cursor = mode === "editor" ? "crosshair" : "pointer";

  return (
    <div
      ref={containerRef}
      className={s.container}
      tabIndex={0}
      style={{ height }}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
      onBlur={handleBlur}
    >
      <div className={s.toolbar}>
        <button
          type="button"
          className={s.toolbarBtn}
          onClick={handleZoomOut}
          aria-label="Zoom out"
        >
          −
        </button>
        <button type="button" className={s.toolbarBtn} onClick={handleZoomIn} aria-label="Zoom in">
          +
        </button>
        <button type="button" className={s.toolbarBtn} onClick={() => fitRef.current()} aria-label="Fit">
          Fit
        </button>
        <span className={s.readout} data-testid="piano-readout">
          1 доля = {Math.round(viewState.pxPerBeat)}px
        </span>
      </div>
      <div className={s.body}>
        <canvas ref={keyRailRef} className={s.keyRail} data-role="piano-keys" />
        <div className={s.mainCol}>
          {mode === "view" ? (
            <div
              ref={markerWrapRef}
              className={s.markerStripWrap}
              style={{ height: markerStripHeight }}
              data-role="piano-marker-strip"
              onPointerDown={handleMarkerWrapPointerDown}
              onMouseDown={handleMarkerWrapPointerDown as unknown as React.MouseEventHandler<HTMLDivElement>}
              onDoubleClick={handleMarkerWrapDoubleClick}
            >
              <canvas ref={markerRef} className={s.markerStrip} data-role="piano-markers" />
              {markerChips.map((m) => (
                <span
                  key={m.id}
                  className={`${s.markerChip} ${activeMarkerId === m.id ? s.markerChipActive : ""}`}
                  style={{ left: m.left }}
                  data-marker-id={m.id}
                  data-testid="piano-marker-chip"
                  onPointerDown={(e) => startMarkerDrag(e, m.id)}
                  onPointerMove={moveMarkerDrag}
                  onPointerUp={endMarkerDrag}
                  onPointerCancel={endMarkerDrag}
                  onMouseDown={startMarkerDrag as unknown as React.MouseEventHandler<HTMLSpanElement>}
                  onMouseMove={moveMarkerDrag as unknown as React.MouseEventHandler<HTMLSpanElement>}
                  onMouseUp={endMarkerDrag as unknown as React.MouseEventHandler<HTMLSpanElement>}
                  onDoubleClick={(e) => {
                    e.stopPropagation();
                    setEditingMarkerId(m.id);
                    setEditingName(m.name);
                  }}
                >
                  <span className={s.markerChipLabel}>
                    #{m.measure}
                    {m.name ? ` ${m.name}` : ""}
                  </span>
                  <button
                    type="button"
                    className={s.markerDelete}
                    data-testid="piano-marker-delete"
                    aria-label={`Удалить метку ${m.measure}`}
                    onPointerDown={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteMarker?.(m.id);
                    }}
                  >
                    ×
                  </button>
                </span>
              ))}
              {editingMarkerId !== null && editingX !== null ? (
                <input
                  className={s.markerInput}
                  style={{ left: editingX }}
                  value={editingName}
                  autoFocus
                  data-testid="piano-marker-input"
                  onPointerDown={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onChange={(e) => setEditingName(e.target.value)}
                  onBlur={commitRename}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      commitRename();
                    } else if (e.key === "Escape") {
                      setEditingMarkerId(null);
                      setEditingName("");
                    }
                  }}
                />
              ) : null}
            </div>
          ) : null}
          <canvas ref={rulerRef} className={s.ruler} data-role="piano-ruler" />
          <div ref={canvasWrapRef} className={s.canvasWrap}>
            <canvas
              ref={canvasRef}
              className={s.canvas}
              data-role="piano-main"
              style={{ cursor, touchAction: "none" }}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
              onPointerLeave={handlePointerLeave}
              onMouseDown={handlePointerDown as unknown as React.MouseEventHandler<HTMLCanvasElement>}
              onMouseMove={handlePointerMove as unknown as React.MouseEventHandler<HTMLCanvasElement>}
              onMouseUp={handlePointerUp as unknown as React.MouseEventHandler<HTMLCanvasElement>}
              onMouseLeave={handlePointerLeave as unknown as React.MouseEventHandler<HTMLCanvasElement>}
            />
            <div ref={hoverRef} className={s.hoverHint} data-role="piano-hover" />
            <div
              className={s.hScrollbar}
              data-testid="piano-hscroll"
              style={{ display: hVisible ? undefined : "none" }}
            >
              <div
                className={s.hThumb}
                style={{ left: thumbX, width: thumbW }}
                onPointerDown={(e) => startThumbDrag(e, "x")}
                onPointerMove={moveThumb}
                onPointerUp={endThumb}
                onPointerCancel={endThumb}
                onMouseDown={startThumbDrag as unknown as React.MouseEventHandler<HTMLDivElement>}
                onMouseMove={moveThumb as unknown as React.MouseEventHandler<HTMLDivElement>}
                onMouseUp={endThumb as unknown as React.MouseEventHandler<HTMLDivElement>}
              />
            </div>
            <div
              className={s.vScrollbar}
              data-testid="piano-vscroll"
              style={{ display: vVisible ? undefined : "none" }}
            >
              <div
                className={s.vThumb}
                style={{ top: thumbY, height: thumbH }}
                onPointerDown={(e) => startThumbDrag(e, "y")}
                onPointerMove={moveThumb}
                onPointerUp={endThumb}
                onPointerCancel={endThumb}
                onMouseDown={startThumbDrag as unknown as React.MouseEventHandler<HTMLDivElement>}
                onMouseMove={moveThumb as unknown as React.MouseEventHandler<HTMLDivElement>}
                onMouseUp={endThumb as unknown as React.MouseEventHandler<HTMLDivElement>}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
